import React, { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * Privacy Policy
 * -----------------------------------------------------------------------------
 * Content is inferred from the live product surface rather than boilerplate:
 *  - Contact / "Book a Demo" forms collect Name, Email and Message
 *    (see BookDemoModal + faq contact form).
 *  - The newsletter widget posts the subscriber email to Sender.net
 *    (see components/Resuseables/NewsLetter) — an email-marketing processor.
 *  - Google Analytics (gtag, G-CC9B4C4PC8) is loaded in public/index.html.
 *  - Images are served from Cloudinary; fonts from Google Fonts.
 *  - Authenticated product access lives at app.sleengshort.com; API docs on GitBook.
 *  - Products: Bulk SMS, USSD, SMS Campaigns, Voice and Airtime — so Sleengshort
 *    also processes end-recipient data (phone numbers/message content) on behalf
 *    of business customers, i.e. it acts as a data *processor* for that data.
 *  - The business operates in Nigeria and the United Kingdom (footer addresses),
 *    hence the NDPA (Nigeria) and UK GDPR references.
 *
 * NOTE (for legal review before launch): the following could not be inferred
 * from the codebase and use conservative placeholders that Legal should confirm:
 *   - Company registration numbers (Nigeria RC / UK company number).
 *   - A named Data Protection Officer / dedicated privacy inbox.
 *   - Exact data-retention durations (values below are conservative defaults).
 *   - The governing-law jurisdiction for disputes.
 * These are intentionally worded conservatively so nothing is over-claimed.
 */

const LAST_UPDATED = "July 17, 2026";
const PRIVACY_EMAIL = "Support@sleengshort.com";

const sections = [
  {
    id: "information-we-collect",
    title: "1. Information We Collect",
    body: (
      <>
        <p>
          We collect information to provide and improve our communication
          services. The types of information we collect depend on how you
          interact with Sleengshort.
        </p>
        <h3 className="policy-subheading">Information you provide to us</h3>
        <ul className="policy-list">
          <li>
            <span className="font-medium">Contact and enquiry details.</span>{" "}
            When you request a demo, contact our support team, or send us a
            message, we collect your name, email address and the content of your
            message.
          </li>
          <li>
            <span className="font-medium">Account information.</span> When you
            register for or log in to our platform at app.sleengshort.com, we
            collect the credentials and business details needed to create and
            secure your account.
          </li>
          <li>
            <span className="font-medium">Newsletter subscriptions.</span> If
            you subscribe to updates, we collect your email address to keep you
            informed about products, updates and promotions.
          </li>
          <li>
            <span className="font-medium">Billing details.</span> If you
            purchase paid services, we and our payment providers collect the
            information required to process your transactions.
          </li>
        </ul>
        <h3 className="policy-subheading">
          Information collected automatically
        </h3>
        <ul className="policy-list">
          <li>
            <span className="font-medium">Usage and device data,</span> such as
            IP address, browser type, device information, pages visited and the
            dates and times of your visits.
          </li>
          <li>
            <span className="font-medium">Cookies and analytics data</span>{" "}
            gathered through cookies and similar technologies (see the Cookies
            section below).
          </li>
        </ul>
        <h3 className="policy-subheading">
          Information you send through our services
        </h3>
        <p>
          When you use our Bulk SMS, USSD, SMS Campaign, Voice or Airtime
          services, you may upload or transmit recipient contact lists, phone
          numbers and message content. We process this information on your
          behalf and in accordance with your instructions. For this data you are
          the data controller and Sleengshort acts as your data processor. You
          are responsible for ensuring you have a lawful basis and appropriate
          consent to send messages to your recipients.
        </p>
      </>
    ),
  },
  {
    id: "how-we-use-information",
    title: "2. How We Use Information",
    body: (
      <>
        <p>We use the information we collect to:</p>
        <ul className="policy-list">
          <li>Provide, operate, maintain and secure our services;</li>
          <li>
            Deliver the messages, campaigns and transactions you request through
            the platform;
          </li>
          <li>
            Create and administer your account and authenticate your access;
          </li>
          <li>
            Respond to your enquiries, demo requests and support tickets;
          </li>
          <li>
            Process payments and send you service-related and transactional
            communications;
          </li>
          <li>
            Send you marketing and newsletter updates where you have opted in
            (you can unsubscribe at any time);
          </li>
          <li>
            Monitor, analyse and improve the performance, reliability and
            usability of our services;
          </li>
          <li>
            Detect, prevent and address fraud, abuse, security incidents and
            technical issues; and
          </li>
          <li>
            Comply with our legal, regulatory and telecommunications obligations.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "legal-bases",
    title: "3. Legal Bases for Processing",
    body: (
      <>
        <p>
          Where data protection law applies (including the UK GDPR and the
          Nigeria Data Protection Act), we rely on one or more of the following
          legal bases to process your personal data:
        </p>
        <ul className="policy-list">
          <li>
            <span className="font-medium">Performance of a contract</span> — to
            provide the services you have requested and manage your account;
          </li>
          <li>
            <span className="font-medium">Consent</span> — for example, to send
            you marketing communications, which you may withdraw at any time;
          </li>
          <li>
            <span className="font-medium">Legitimate interests</span> — to
            secure, improve and promote our services, provided your rights do
            not override those interests; and
          </li>
          <li>
            <span className="font-medium">Legal obligation</span> — where we are
            required to process data to comply with applicable law.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    title: "4. Cookies and Tracking Technologies",
    body: (
      <>
        <p>
          We use cookies and similar technologies to operate our website,
          remember your preferences and understand how our services are used.
          The categories we use include:
        </p>
        <ul className="policy-list">
          <li>
            <span className="font-medium">Essential cookies</span> that are
            necessary for the website and platform to function and to keep your
            session secure;
          </li>
          <li>
            <span className="font-medium">Analytics cookies</span> that help us
            measure traffic and usage. We use Google Analytics for this purpose,
            which sets cookies to collect aggregated, statistical information
            about visitors; and
          </li>
          <li>
            <span className="font-medium">Preference cookies</span> that
            remember choices you make to improve your experience.
          </li>
        </ul>
        <p>
          You can control or delete cookies through your browser settings.
          Disabling some cookies may affect the functionality of our website.
        </p>
      </>
    ),
  },
  {
    id: "data-sharing",
    title: "5. Data Sharing and Disclosure",
    body: (
      <>
        <p>
          We do not sell your personal data. We share information only in the
          following circumstances:
        </p>
        <ul className="policy-list">
          <li>
            <span className="font-medium">Service providers</span> who process
            data on our behalf under contract, such as hosting, analytics, email
            delivery, telecommunications carriers and payment processors;
          </li>
          <li>
            <span className="font-medium">
              Mobile network operators and aggregators
            </span>{" "}
            that are necessary to deliver the SMS, USSD, Voice and Airtime
            messages you send;
          </li>
          <li>
            <span className="font-medium">Legal and regulatory authorities</span>{" "}
            where we are required to disclose information by law, regulation or
            valid legal process;
          </li>
          <li>
            <span className="font-medium">Business transfers</span>, in
            connection with a merger, acquisition or sale of assets, subject to
            this Privacy Policy; and
          </li>
          <li>
            <span className="font-medium">With your consent</span>, or at your
            direction.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "third-party-services",
    title: "6. Third-Party Services",
    body: (
      <>
        <p>
          We rely on trusted third parties to deliver parts of our services.
          These providers process data only as needed to perform their
          functions and are bound by their own privacy commitments:
        </p>
        <ul className="policy-list">
          <li>
            <span className="font-medium">Google Analytics</span> — website
            usage and traffic analytics;
          </li>
          <li>
            <span className="font-medium">Sender.net</span> — delivery of
            newsletter and email subscriptions;
          </li>
          <li>
            <span className="font-medium">Cloudinary</span> — image hosting and
            delivery;
          </li>
          <li>
            <span className="font-medium">Google Fonts</span> — website
            typography; and
          </li>
          <li>
            <span className="font-medium">
              Mobile network operators and messaging aggregators
            </span>{" "}
            — routing and delivery of your messages.
          </li>
        </ul>
        <p>
          Our website may contain links to third-party sites, such as our API
          documentation and social media pages. This Privacy Policy does not
          apply to those sites, and we encourage you to review their privacy
          policies.
        </p>
      </>
    ),
  },
  {
    id: "international-transfers",
    title: "7. International Data Transfers",
    body: (
      <>
        <p>
          Sleengshort operates in Nigeria and the United Kingdom, and our
          service providers may be located in other countries. As a result, your
          information may be transferred to, stored in and processed in
          countries other than your own. Where we transfer personal data across
          borders, we take steps to ensure it is protected by appropriate
          safeguards consistent with applicable data protection law.
        </p>
      </>
    ),
  },
  {
    id: "data-security",
    title: "8. Data Security",
    body: (
      <>
        <p>
          We implement appropriate technical and organisational measures to
          protect your information against unauthorised access, loss, misuse or
          alteration. These include encryption in transit, access controls and
          secure authentication for our platform. However, no method of
          transmission or storage is completely secure, and we cannot guarantee
          absolute security.
        </p>
      </>
    ),
  },
  {
    id: "data-retention",
    title: "9. Data Retention",
    body: (
      <>
        <p>
          We retain personal data only for as long as necessary to fulfil the
          purposes described in this Privacy Policy, including to provide our
          services, comply with our legal, tax and regulatory obligations,
          resolve disputes and enforce our agreements. When data is no longer
          needed, we securely delete or anonymise it.
        </p>
        <p>
          Message and campaign data that you process through our platform is
          retained in accordance with your account settings and our agreement
          with you. {/* NOTE: confirm exact retention windows with Legal. */}
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "10. Your Rights",
    body: (
      <>
        <p>
          Depending on your location and applicable law, you may have the
          following rights regarding your personal data:
        </p>
        <ul className="policy-list">
          <li>Access the personal data we hold about you;</li>
          <li>Request correction of inaccurate or incomplete data;</li>
          <li>Request deletion of your personal data;</li>
          <li>Object to or restrict certain processing;</li>
          <li>Request a portable copy of your data;</li>
          <li>
            Withdraw consent at any time where processing is based on consent;
            and
          </li>
          <li>
            Lodge a complaint with your local data protection authority.
          </li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{" "}
          <a href={`mailto:${PRIVACY_EMAIL}`} className="policy-link">
            {PRIVACY_EMAIL}
          </a>
          . We will respond in line with applicable law.
        </p>
      </>
    ),
  },
  {
    id: "childrens-privacy",
    title: "11. Children's Privacy",
    body: (
      <>
        <p>
          Our services are intended for businesses and are not directed to
          children. We do not knowingly collect personal data from children. If
          you believe a child has provided us with personal data, please contact
          us and we will take appropriate steps to delete it.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "12. Changes to This Privacy Policy",
    body: (
      <>
        <p>
          We may update this Privacy Policy from time to time to reflect changes
          in our practices, technology or legal requirements. When we make
          material changes, we will update the "Last updated" date above and,
          where appropriate, notify you. We encourage you to review this page
          periodically.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "13. Contact Information",
    body: (
      <>
        <p>
          If you have questions or concerns about this Privacy Policy or how we
          handle your information, please contact us:
        </p>
        <ul className="policy-list">
          <li>
            <span className="font-medium">Email:</span>{" "}
            <a href={`mailto:${PRIVACY_EMAIL}`} className="policy-link">
              {PRIVACY_EMAIL}
            </a>
          </li>
          <li>
            <span className="font-medium">Phone:</span> +234 811 4159 025
            (Nigeria) &middot; +44 7944 554720 (United Kingdom)
          </li>
          <li>
            <span className="font-medium">Nigeria:</span> 4A, Olaide Omotoro
            Street off CIS road, off Amore, Lekki, Lagos
          </li>
          <li>
            <span className="font-medium">United Kingdom:</span> 71-75 Shelton
            Street, Covent Garden, London
          </li>
        </ul>
      </>
    ),
  },
];

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    const previousTitle = document.title;
    document.title = "Privacy Policy | Sleengshort";

    const metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription
      ? metaDescription.getAttribute("content")
      : null;
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Sleengshort's Privacy Policy explains what information we collect, how we use it, and the rights you have over your data."
      );
    }

    return () => {
      document.title = previousTitle;
      if (metaDescription && previousDescription !== null) {
        metaDescription.setAttribute("content", previousDescription);
      }
    };
  }, []);

  return (
    <main className="max-w-screen-xl mx-auto py-14">
      {/* heading */}
      <section className="md:space-y-10 space-y-2 px-8 text-center">
        <h1 className="capitalize text-xl md:text-7xl font-semibold text-primary">
          Privacy Policy
        </h1>
        <p className="font-light md:text-xl text-2xs">
          Your privacy matters to us. This policy explains what information
          Sleengshort collects, how we use and protect it, and the choices and
          rights you have.
        </p>
        <p className="text-secondary font-medium text-2xs md:text-base">
          Last updated: {LAST_UPDATED}
        </p>
      </section>

      <div className="mt-10 md:mt-20 mx-6 md:mx-0 flex flex-col lg:flex-row gap-8">
        {/* table of contents */}
        <nav
          aria-label="Privacy Policy sections"
          className="bg-[#B6DCFE] rounded-lg p-6 md:p-8 lg:w-72 lg:shrink-0 h-fit lg:sticky lg:top-6"
        >
          <h2 className="font-semibold text-base md:text-xl text-primary mb-4">
            On this page
          </h2>
          <ul className="space-y-2">
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="text-xs md:text-sm hover:text-primary hover:underline"
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* policy body */}
        <article className="bg-white rounded-lg p-6 md:p-12 flex-1 space-y-10 text-sm md:text-base leading-relaxed text-[#333]">
          <section aria-label="Introduction" className="space-y-4">
            <p>
              This Privacy Policy describes how Sleengshort LTD ("Sleengshort",
              "we", "us" or "our") collects, uses, shares and protects
              information when you visit our website, use our communication
              services &mdash; including Bulk SMS, USSD, SMS Campaigns, Voice and
              Airtime &mdash; or otherwise interact with us. By using our website
              or services, you agree to the practices described in this policy.
            </p>
          </section>

          {sections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              aria-labelledby={`${section.id}-heading`}
              className="space-y-4 scroll-mt-6"
            >
              <h2
                id={`${section.id}-heading`}
                className="font-semibold text-lg md:text-2xl text-primary"
              >
                {section.title}
              </h2>
              {section.body}
            </section>
          ))}

          <p className="pt-4">
            Looking for more? Visit our{" "}
            <Link
              to="/faq"
              onClick={() => window.scrollTo(0, 0)}
              className="policy-link"
            >
              Support
            </Link>{" "}
            page or reach our team at{" "}
            <a href={`mailto:${PRIVACY_EMAIL}`} className="policy-link">
              {PRIVACY_EMAIL}
            </a>
            .
          </p>
        </article>
      </div>
    </main>
  );
};

export default PrivacyPolicyPage;
